import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Background,
  BackgroundVariant,
  ConnectionLineType,
  ConnectionMode,
  Controls,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type Connection,
  type FinalConnectionState,
  type OnSelectionChangeParams,
} from "@xyflow/react";
import type { DecryptedProjectGraph } from "../projects-view.js";
import {
  buildProjectFlow,
  layoutPositions,
  trafficEdges,
  wireEdge,
  withActivity,
  type BatonEdge,
  type BatonNode,
  type BuildOptions,
} from "./model.js";
import type { ActivityEvent } from "./activity.js";
import { classifyConnection, isValidConnection as validConnection } from "./connections.js";
import { BATON_NODE_TYPES, useFlowActions } from "./nodes.js";
import { BATON_EDGE_TYPES } from "./animated-message-edge.js";
import type { PositionChange } from "../hooks/use-canvas-positions.js";

export interface FlowViewer {
  userId: string;
  generalAgentId: string | null;
  isAdmin: boolean;
  isOwner: boolean;
}

export interface FlowCanvasProps {
  graph: DecryptedProjectGraph;
  viewer: FlowViewer;
  positions: ReadonlyMap<string, { x: number; y: number }>;
  onPositionsChange(changes: PositionChange[]): void;
  onTidy(positions: PositionChange[]): void;
  events: ActivityEvent[];
  onSelectionChange(node: BatonNode | null): void;
  ariaLabel: string;
}

type MenuState =
  | { kind: "pane"; x: number; y: number; flow: { x: number; y: number } }
  | { kind: "node"; x: number; y: number; node: BatonNode }
  | { kind: "edge"; x: number; y: number; edge: BatonEdge };

/** The build options the viewer implies; shared with the sidebar's "Tidy up". */
export function buildOptionsFor(viewer: FlowViewer): Omit<BuildOptions, "positions"> {
  return { viewerUserId: viewer.userId, viewerGeneralAgentId: viewer.generalAgentId, isAdmin: viewer.isAdmin };
}

/** The editable project canvas: drag to arrange, drag between handles to wire or assign, right-click for more. */
export function FlowCanvas(props: FlowCanvasProps) {
  return <ReactFlowProvider><FlowCanvasInner {...props} /></ReactFlowProvider>;
}

function FlowCanvasInner(props: FlowCanvasProps) {
  const actions = useFlowActions();
  const { screenToFlowPosition, fitView } = useReactFlow<BatonNode, BatonEdge>();
  const wrapper = useRef<HTMLDivElement | null>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState<BatonNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<BatonEdge>([]);
  const [menu, setMenu] = useState<MenuState | null>(null);
  const nodesRef = useRef<BatonNode[]>([]);
  const edgesRef = useRef<BatonEdge[]>([]);
  nodesRef.current = nodes;
  edgesRef.current = edges;

  const buildOptions = useMemo(() => buildOptionsFor(props.viewer), [props.viewer]);

  const flow = useMemo(
    () => buildProjectFlow(props.graph, { ...buildOptions, positions: props.positions }),
    [props.graph, buildOptions, props.positions],
  );

  useEffect(() => {
    setNodes((current) => {
      const selected = new Set(current.filter((node) => node.selected).map((node) => node.id));
      return flow.nodes.map((node) => selected.has(node.id) ? { ...node, selected: true } : node);
    });
    setEdges(flow.edges);
  }, [flow, setNodes, setEdges]);

  const liveEdges = useMemo(
    () => withActivity([...edges, ...trafficEdges(props.events, nodes)], props.events),
    [edges, nodes, props.events],
  );

  const isValidConnection = useCallback((candidate: BatonEdge | Connection) => validConnection(
    { source: candidate.source, target: candidate.target, sourceHandle: candidate.sourceHandle, targetHandle: candidate.targetHandle },
    nodesRef.current,
    edgesRef.current,
    { userId: props.viewer.userId },
  ), [props.viewer.userId]);

  const onConnect = useCallback((connection: Connection) => {
    const intent = classifyConnection(connection, nodesRef.current, edgesRef.current, { userId: props.viewer.userId });
    if (intent.kind === "wire") {
      const id = crypto.randomUUID();
      const optimistic = wireEdge({ id, sourceAgentId: intent.sourceAgentId, targetAgentId: intent.targetAgentId, createdByUserId: props.viewer.userId }, true);
      setEdges((current) => [...current, optimistic]);
      void actions.createWire({ id, sourceAgentId: intent.sourceAgentId, targetAgentId: intent.targetAgentId })
        .catch(() => setEdges((current) => current.filter((edge) => edge.id !== optimistic.id)));
      return;
    }
    if (intent.kind === "assignment") void actions.assign(intent.agentId, intent.repositoryId);
  }, [actions, props.viewer.userId, setEdges]);

  /** Dragging a wire from an agent onto empty canvas creates a new agent there, pre-wired. */
  const onConnectEnd = useCallback((event: MouseEvent | TouchEvent, state: FinalConnectionState) => {
    if (state.toNode || !state.fromNode || state.fromNode.type !== "agent" || state.fromHandle?.id !== "out") return;
    const point = "changedTouches" in event ? event.changedTouches[0] : event;
    if (!point) return;
    const from = state.fromNode as unknown as BatonNode;
    if (from.data.kind !== "agent") return;
    actions.createAgent({
      position: screenToFlowPosition({ x: point.clientX, y: point.clientY }),
      wireFromAgentId: from.data.agentId,
    });
  }, [actions, screenToFlowPosition]);

  const onNodeDragStop = useCallback((_event: unknown, _node: BatonNode, dragged: BatonNode[]) => {
    props.onPositionsChange(dragged.map((node) => ({ nodeId: node.id, x: node.position.x, y: node.position.y })));
  }, [props]);

  const onBeforeDelete = useCallback(async ({ edges: candidates }: { nodes: BatonNode[]; edges: BatonEdge[] }) => {
    const removable = candidates.filter((edge) => edge.data?.removable && (edge.data.relation === "wire" || edge.data.relation === "assignment"));
    if (removable.length === 0) return false;
    const assignments = removable.filter((edge) => edge.data?.relation === "assignment");
    if (assignments.length > 0 && !window.confirm(`Remove ${assignments.length === 1 ? "this assignment" : `${assignments.length} assignments`} from the project?`)) {
      return false;
    }
    return { nodes: [], edges: removable };
  }, []);

  const onEdgesDelete = useCallback((deleted: BatonEdge[]) => {
    for (const edge of deleted) {
      if (!edge.data?.refId) continue;
      if (edge.data.relation === "wire") void actions.removeWire(edge.data.refId);
      if (edge.data.relation === "assignment") void actions.removeAssignment(edge.data.refId);
    }
  }, [actions]);

  const onEdgeClick = useCallback((_event: unknown, edge: BatonEdge) => {
    if (edge.data?.relation === "wire" && edge.data.refId && edge.data.sourceAgentId && edge.data.targetAgentId) {
      actions.openWire({ wireId: edge.data.refId, sourceAgentId: edge.data.sourceAgentId, targetAgentId: edge.data.targetAgentId });
    }
  }, [actions]);

  const onSelectionChange = useCallback((params: OnSelectionChangeParams) => {
    props.onSelectionChange((params.nodes[0] as BatonNode | undefined) ?? null);
  }, [props]);

  const menuAt = (event: { clientX: number; clientY: number; preventDefault(): void }) => {
    event.preventDefault();
    const rect = wrapper.current?.getBoundingClientRect();
    return { x: event.clientX - (rect?.left ?? 0), y: event.clientY - (rect?.top ?? 0) };
  };

  useEffect(() => {
    if (!menu) return;
    const close = (event: KeyboardEvent) => { if (event.key === "Escape") setMenu(null); };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [menu]);

  const tidy = () => props.onTidy(layoutPositions(props.graph, buildOptions));
  const projectLabel = props.graph.project.name;

  return <div className="flow-canvas" data-testid="flow-canvas" ref={wrapper}>
    <ReactFlow
      nodes={nodes}
      edges={liveEdges}
      nodeTypes={BATON_NODE_TYPES}
      edgeTypes={BATON_EDGE_TYPES}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onSelectionChange={onSelectionChange}
      nodesConnectable
      connectionMode={ConnectionMode.Strict}
      connectionLineType={ConnectionLineType.Bezier}
      connectionLineStyle={{ stroke: "var(--brand)", strokeWidth: 2, strokeDasharray: "6 4" }}
      connectionRadius={28}
      isValidConnection={isValidConnection}
      onConnect={onConnect}
      onConnectEnd={onConnectEnd}
      onNodeDragStop={onNodeDragStop}
      onBeforeDelete={onBeforeDelete}
      onEdgesDelete={onEdgesDelete}
      onEdgeClick={onEdgeClick}
      onPaneClick={() => setMenu(null)}
      onMoveStart={() => setMenu(null)}
      onPaneContextMenu={(event) => {
        const at = menuAt(event as unknown as MouseEvent);
        setMenu({ kind: "pane", ...at, flow: screenToFlowPosition({ x: (event as MouseEvent).clientX, y: (event as MouseEvent).clientY }) });
      }}
      onNodeContextMenu={(event, node) => setMenu({ kind: "node", ...menuAt(event), node: node as BatonNode })}
      onEdgeContextMenu={(event, edge) => setMenu({ kind: "edge", ...menuAt(event), edge: edge as BatonEdge })}
      deleteKeyCode={["Backspace", "Delete"]}
      edgesReconnectable={false}
      proOptions={{ hideAttribution: false }}
      fitView
      fitViewOptions={{ padding: 0.18, maxZoom: 1 }}
      minZoom={0.3}
      maxZoom={1.6}
      aria-label={props.ariaLabel}
    >
      <Background variant={BackgroundVariant.Dots} gap={22} size={1} color="#cbd3de" />
      <Controls showInteractive={false} position="bottom-right" />
    </ReactFlow>
    {menu && <ul className="flow-menu" role="menu" style={{ left: menu.x, top: menu.y }} onClick={() => setMenu(null)}>
      {menu.kind === "pane" && <>
        <li><button type="button" role="menuitem" onClick={() => actions.createAgent({ position: menu.flow })}>+ Agent here</button></li>
        {actions.permissions.isAdmin && <li><button type="button" role="menuitem" onClick={() => actions.addRepository()}>+ Repository</button></li>}
        {actions.permissions.isAdmin && <li><button type="button" role="menuitem" onClick={() => actions.invite()}>Invite people</button></li>}
        <li><button type="button" role="menuitem" onClick={tidy}>Tidy up</button></li>
        <li><button type="button" role="menuitem" onClick={() => void fitView({ padding: 0.18, maxZoom: 1 })}>Fit view</button></li>
      </>}
      {menu.kind === "node" && menu.node.data.kind === "agent" && <>
        {!menu.node.data.isViewerGeneral && <li><button type="button" role="menuitem" onClick={() => actions.chat({ agentId: menu.node.data.agentId as string, handle: menu.node.data.handle as string })}>Chat</button></li>}
        {menu.node.data.ownedByViewer && <li><button type="button" role="menuitem" onClick={() => actions.connect({ agentId: menu.node.data.agentId as string, handle: menu.node.data.handle as string })}>Connect Claude Code or Codex</button></li>}
      </>}
      {menu.kind === "node" && menu.node.data.kind === "repository" && <>
        {actions.ownAgents.map((agent) => <li key={agent.agentId}>
          <button type="button" role="menuitem" onClick={() => void actions.assign(agent.agentId, menu.node.data.repositoryId as string)}>Assign {agent.handle}</button>
        </li>)}
        {actions.permissions.isAdmin && <li>
          <button type="button" role="menuitem" onClick={() => actions.unlinkRepository(menu.node.data.repositoryId as string, `${menu.node.data.ownerHandle}/${menu.node.data.name}`)}>Unlink repository</button>
        </li>}
      </>}
      {menu.kind === "edge" && menu.edge.data?.relation === "wire" && menu.edge.data.refId && <>
        <li><button type="button" role="menuitem" onClick={() => actions.openWire({ wireId: menu.edge.data!.refId!, sourceAgentId: menu.edge.data!.sourceAgentId!, targetAgentId: menu.edge.data!.targetAgentId! })}>Open chat</button></li>
        {menu.edge.data.removable && <li><button type="button" role="menuitem" onClick={() => void actions.removeWire(menu.edge.data!.refId!)}>Remove wire</button></li>}
      </>}
      {menu.kind === "edge" && menu.edge.data?.relation === "assignment" && menu.edge.data.removable && menu.edge.data.refId && <>
        <li><button type="button" role="menuitem" onClick={() => { if (window.confirm("Remove this assignment from the project?")) void actions.removeAssignment(menu.edge.data!.refId!); }}>Remove assignment</button></li>
      </>}
      <li className="flow-menu-hint muted">{projectLabel}</li>
    </ul>}
  </div>;
}
